import { useMemo, useState } from 'react';
import { useDeleteContribution, useLeverageClients, useLeverageContributions } from '@/hooks/leverageQueries';
import { useToast } from '@/hooks/useToast';
import { ConfirmDialog, ErrorState, Pagination, SectionHead, StatLine, TableSkeleton } from '@/components/ui';
import { IconArrowDown, IconArrowUp, IconEdit, IconPlus, IconSearch, IconTrash } from '@/components/Icons';
import { formatBRL, formatBRLShort, formatDate, formatNumber } from '@/lib/format';
import { FULL_BP, formatBp } from '@/lib/leverage';
import type { LeverageClient, LeverageContribution } from '@/lib/types';
import { ContributionModal } from './ContributionModal';

const PAGE_SIZE = 25;

type SortKey = 'name' | 'owner' | 'capital' | 'allocated' | 'last';
type SortDir = 'asc' | 'desc';
interface Sort {
  key: SortKey;
  dir: SortDir;
}

interface Column {
  key: SortKey;
  label: string;
  right?: boolean;
  adminOnly?: boolean;
  /** O primeiro clique: texto de A a Z; número e data, do maior e do mais recente. */
  first: SortDir;
  asc: string;
  desc: string;
}

const COLUMNS: Column[] = [
  { key: 'name', label: 'Cliente', first: 'asc', asc: 'de A a Z', desc: 'de Z a A' },
  { key: 'owner', label: 'Responsável', adminOnly: true, first: 'asc', asc: 'de A a Z', desc: 'de Z a A' },
  { key: 'capital', label: 'Capital', right: true, first: 'desc', asc: 'menor primeiro', desc: 'maior primeiro' },
  { key: 'allocated', label: 'Em operações abertas', right: true, adminOnly: true, first: 'desc', asc: 'menos primeiro', desc: 'mais primeiro' },
  { key: 'last', label: 'Último aporte', right: true, first: 'desc', asc: 'mais antigo primeiro', desc: 'mais recente primeiro' },
];

/** O valor do filtro de responsável para os clientes sem responsável. */
const NO_OWNER = '__sem-responsavel';

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });
const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const isIncomplete = (client: LeverageClient) => !(client.lead.accountLogin && client.lead.hasAccountPassword);

function compareBy(key: SortKey, a: LeverageClient, b: LeverageClient): number {
  switch (key) {
    case 'name':
      return collator.compare(a.lead.name, b.lead.name);
    case 'owner':
      return collator.compare(a.lead.ownerName ?? '', b.lead.ownerName ?? '');
    case 'capital':
      return a.capital - b.capital;
    case 'allocated':
      return (a.allocatedBp ?? 0) - (b.allocatedBp ?? 0);
    case 'last':
      return a.lastContributionDate.localeCompare(b.lastContributionDate);
  }
}

/** Ordena pela coluna; no empate, pelo nome. Sem responsável fica no fim nos dois sentidos. */
function sortClients(items: LeverageClient[], { key, dir }: Sort): LeverageClient[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    if (key === 'owner' && !a.lead.ownerName !== !b.lead.ownerName) return a.lead.ownerName ? -1 : 1;
    return sign * compareBy(key, a, b) || collator.compare(a.lead.name, b.lead.name);
  });
}

/**
 * Os aportes: o capital de cada cliente (a soma dos aportes) e o histórico.
 * O membro vê os clientes dele; o administrador vê todos, quanto de cada
 * capital já está nas operações abertas, e corrige ou exclui um aporte.
 */
export function ContributionsPanel({ isAdmin, onCreate }: { isAdmin: boolean; onCreate: () => void }) {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<LeverageContribution | null>(null);
  const [removing, setRemoving] = useState<LeverageContribution | null>(null);
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [sort, setSort] = useState<Sort>({ key: 'name', dir: 'asc' });
  const clients = useLeverageClients();
  const history = useLeverageContributions({ page, pageSize: PAGE_SIZE });
  const remove = useDeleteContribution();

  const items = useMemo(() => clients.data?.items ?? [], [clients.data]);
  const columns = COLUMNS.filter((column) => isAdmin || !column.adminOnly);

  const owners = useMemo(
    () => [...new Set(items.flatMap((item) => (item.lead.ownerName ? [item.lead.ownerName] : [])))].sort(collator.compare),
    [items],
  );
  const hasNoOwner = items.some((item) => !item.lead.ownerName);
  // O responsável escolhido pode sumir da lista num recarregamento: aí o filtro volta a "todos".
  const ownerFilter = owners.includes(owner) || (owner === NO_OWNER && hasNoOwner) ? owner : '';

  const visible = useMemo(() => {
    const term = normalize(query.trim());
    const filtered = items.filter(
      (item) =>
        (!term || normalize(`${item.lead.name} ${item.lead.ownerName ?? ''}`).includes(term)) &&
        (!ownerFilter || (ownerFilter === NO_OWNER ? !item.lead.ownerName : item.lead.ownerName === ownerFilter)) &&
        (!onlyIncomplete || isIncomplete(item)),
    );
    return sortClients(filtered, sort);
  }, [items, query, ownerFilter, onlyIncomplete, sort]);

  if (clients.error) return <ErrorState message={(clients.error as Error).message} />;

  const capital = items.reduce((sum, item) => sum + item.capital, 0);
  const contributions = items.reduce((sum, item) => sum + item.contributionCount, 0);
  const filtering = Boolean(query.trim() || ownerFilter || onlyIncomplete);
  const visibleCapital = visible.reduce((sum, item) => sum + item.capital, 0);

  function toggleSort(column: Column) {
    setSort((current) =>
      current.key === column.key ? { key: column.key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key: column.key, dir: column.first },
    );
  }

  function clearFilters() {
    setQuery('');
    setOwner('');
    setOnlyIncomplete(false);
  }

  async function confirmRemove(contribution: LeverageContribution) {
    try {
      await remove.mutateAsync(contribution.id);
      toast.success('Aporte excluído.');
      setRemoving(null);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <>
      {clients.data ? (
        <div className="metric-row" style={{ marginBottom: 28 }}>
          <StatLine label="Capital aportado" value={formatBRLShort(capital)} color="var(--emphasis)" sub="soma de todos os aportes" />
          <StatLine label="Clientes" value={formatNumber(items.length)} sub="com aporte" />
          <StatLine label="Aportes" value={formatNumber(contributions)} />
        </div>
      ) : null}

      <SectionHead title="Capital por cliente">
        {filtering && items.length > 0 ? (
          <span className="muted num" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {formatNumber(visible.length)} de {formatNumber(items.length)} · {formatBRL(visibleCapital)}
          </span>
        ) : null}
        {/* Sempre montado, para o leitor de tela anunciar quando o filtro muda; fora do fluxo, não abre espaço. */}
        <span className="sr-only" role="status">
          {filtering && items.length > 0 ? `${visible.length} de ${items.length} clientes` : ''}
        </span>
      </SectionHead>
      {items.length > 0 ? (
        <div className="toolbar" style={{ marginBottom: 14 }}>
          <div className="search-input toolbar-search">
            <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
            <input
              className="inp"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={isAdmin ? 'Buscar por cliente ou responsável' : 'Buscar cliente'}
              aria-label="Buscar clientes"
            />
          </div>
          {isAdmin ? (
            <select className="inp" value={ownerFilter} onChange={(event) => setOwner(event.target.value)} aria-label="Responsável">
              <option value="">Todos os responsáveis</option>
              {owners.map((name) => <option key={name} value={name}>{name}</option>)}
              {hasNoOwner ? <option value={NO_OWNER}>Sem responsável</option> : null}
            </select>
          ) : null}
          <select
            className="inp lev-sort-select"
            value={`${sort.key}:${sort.dir}`}
            onChange={(event) => {
              const [key, dir] = event.target.value.split(':') as [SortKey, SortDir];
              setSort({ key, dir });
            }}
            aria-label="Ordenar por"
          >
            {columns.flatMap((column) =>
              (column.first === 'asc' ? (['asc', 'desc'] as const) : (['desc', 'asc'] as const)).map((dir) => (
                <option key={`${column.key}:${dir}`} value={`${column.key}:${dir}`}>{column.label}, {column[dir]}</option>
              )),
            )}
          </select>
          <label className="chip">
            <input type="checkbox" checked={onlyIncomplete} onChange={(event) => setOnlyIncomplete(event.target.checked)} />
            Só conta incompleta
          </label>
        </div>
      ) : null}
      {clients.isLoading ? (
        <TableSkeleton rows={4} columns={isAdmin ? 5 : 4} />
      ) : items.length === 0 ? (
        <div className="empty">
          <p style={{ margin: '0 0 14px' }}>Nenhum aporte registrado ainda.</p>
          <button type="button" className="btn btn-primary" onClick={onCreate}>
            <IconPlus /> Nova alavancagem
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="empty" style={{ marginBottom: 32 }}>
          <p style={{ margin: '0 0 14px' }}>Nenhum cliente com esse filtro.</p>
          <button type="button" className="btn" onClick={clearFilters}>Limpar filtros</button>
        </div>
      ) : (
        <div className="table-wrap" style={{ marginBottom: 32 }}>
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                {columns.map((column) => {
                  const active = sort.key === column.key;
                  return (
                    <th
                      key={column.key}
                      className={column.right ? 'right' : undefined}
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      <button type="button" className="lev-sort" onClick={() => toggleSort(column)}>
                        {column.label}
                        <span className="lev-sort-arrow" aria-hidden="true">
                          {active && sort.dir === 'asc' ? <IconArrowUp size={11} /> : <IconArrowDown size={11} />}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item.lead.id}>
                  <td className="cell-main">
                    <span style={{ fontWeight: 500 }}>{item.lead.name}</span>
                    <div className="muted lev-exec-sub">
                      {item.contributionCount} {item.contributionCount === 1 ? 'aporte' : 'aportes'}
                      {isIncomplete(item) ? ' · conta da corretora incompleta' : ''}
                    </div>
                  </td>
                  {isAdmin ? <td data-label="Responsável">{item.lead.ownerName ?? <span className="muted">Sem responsável</span>}</td> : null}
                  <td className="num right nowrap cell-key emphasis" style={{ fontWeight: 500 }}>{formatBRL(item.capital)}</td>
                  {isAdmin ? (
                    <td className="num right nowrap" data-label="Em operações">
                      {item.allocatedBp ? formatBp(item.allocatedBp) : <span className="muted">—</span>}
                      {item.allocatedBp && item.allocatedBp >= FULL_BP ? <span className="chip chip-warning" style={{ marginLeft: 6 }}>Todo usado</span> : null}
                    </td>
                  ) : null}
                  <td className="num right nowrap" data-label="Último aporte">{formatDate(item.lastContributionDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length > 0 ? (
        <>
          <SectionHead title="Histórico de aportes" />
          {history.isLoading && !history.data ? (
            <TableSkeleton rows={5} columns={5} />
          ) : (
            <>
              <div className="table-wrap">
                <table className="tbl tbl-cards">
                  <thead>
                    <tr>
                      <th className="nowrap">Data</th>
                      <th>Cliente</th>
                      <th className="right">Valor</th>
                      <th>Registrado por</th>
                      {isAdmin ? <th className="right"><span className="sr-only">Ações</span></th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {history.data?.items.map((contribution) => (
                      <tr key={contribution.id}>
                        <td className="num nowrap" data-label="Data">
                          {formatDate(contribution.date)} <span className="muted">{contribution.time}</span>
                        </td>
                        <td className="cell-main">
                          <span style={{ fontWeight: 500 }}>{contribution.lead.name}</span>
                          {contribution.note ? <div className="muted lev-exec-sub truncate" style={{ maxWidth: 320 }}>{contribution.note}</div> : null}
                        </td>
                        <td className="num right nowrap cell-key">{formatBRL(contribution.amount)}</td>
                        <td data-label="Registrado por">{contribution.createdBy.name}</td>
                        {isAdmin && contribution.receiptId ? (
                          <td className="right nowrap cell-actions muted" style={{ fontSize: 12 }} title="Veio de um comprovante de depósito: corrija ou exclua pelo comprovante.">
                            Do comprovante
                          </td>
                        ) : isAdmin ? (
                          <td className="right nowrap cell-actions">
                            <button type="button" className="icon-btn" aria-label={`Corrigir o aporte de ${contribution.lead.name}`} onClick={() => setEditing(contribution)}>
                              <IconEdit />
                            </button>
                            <button type="button" className="icon-btn" aria-label={`Excluir o aporte de ${contribution.lead.name}`} onClick={() => setRemoving(contribution)}>
                              <IconTrash />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {history.data ? (
                <Pagination page={history.data.page} totalPages={history.data.totalPages} total={history.data.total} onChange={setPage} />
              ) : null}
            </>
          )}
        </>
      ) : null}

      {editing ? <ContributionModal contribution={editing} onClose={() => setEditing(null)} /> : null}
      {removing ? (
        <ConfirmDialog
          title="Excluir o aporte?"
          message={
            <>
              O aporte de <strong className="num">{formatBRL(removing.amount)}</strong> de {removing.lead.name} sai do capital. As operações já
              montadas guardam o valor que tinham.
            </>
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={() => void confirmRemove(removing)}
        />
      ) : null}
    </>
  );
}
